import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

type DrawerSheetSize = "sm" | "md" | "lg";

/** Desktop side-drawer width per size (mobile is always full-width). */
const desktopWidthClass: Record<DrawerSheetSize, string> = {
  sm: "md:w-[380px]",
  md: "md:w-[460px]",
  lg: "md:w-[560px]",
};

/** Duration for the close (exit) animation — fast and snappy. */
const CLOSE_DURATION_MS = 250;
const SWIPE_CLOSE_THRESHOLD_PX = 120;

type Props = {
  isOpen: boolean;
  /**
   * Called when the user dismisses (backdrop click, swipe down, Escape, ✕).
   * The parent owns the open state and must set isOpen=false in response.
   */
  onClose?: () => void;
  size?: DrawerSheetSize;
  icon?: React.ReactNode;
  /** Icon tile tone — "accent" (solid fill, default) or "muted" (soft surface). */
  iconTone?: "accent" | "muted";
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  /** aria-label for the ✕ close button. */
  closeLabel?: string;
  /** Sticky footer content (e.g. Reset / Apply buttons). */
  footerActions?: React.ReactNode;
  children: React.ReactNode;
};

/**
 * Responsive panel for *contextual* tasks — filtering, side detail, anything the
 * user does while wanting to keep the page behind it in view (DESIGN_CONTEXT §12).
 *
 * Mobile  → bottom sheet (slides up, draggable down to dismiss).
 * Desktop → side drawer anchored at the inline-end edge, full height. The
 *           backdrop is lighter on desktop so the underlying list stays readable
 *           while filtering.
 *
 * Use ModalSheet (centered modal on desktop) instead for *focused* tasks that
 * should block the page — forms, confirmations, destructive actions.
 *
 * Mirrors ModalSheet's lifecycle: backdrop click, Escape, swipe-down dismiss,
 * drag handle, paint-frame-guaranteed enter/exit animation, iOS safe area.
 */
export function DrawerSheet({
  isOpen,
  onClose,
  size = "md",
  icon,
  iconTone = "accent",
  title,
  subtitle,
  closeLabel,
  footerActions,
  children,
}: Props) {
  const [isMounted, setIsMounted] = useState(isOpen);
  const [isVisible, setIsVisible] = useState(false);
  // Desktop side-drawer slide direction. The positioner's `justify-end` already
  // anchors the panel at the inline-end (right in LTR, left in RTL); compute the
  // off-screen transform explicitly rather than relying on `ltr:`/`rtl:` Tailwind
  // variants (which slid the wrong way in Arabic).
  const closedSlideX =
    typeof document !== "undefined" && document.documentElement.dir === "rtl"
      ? "md:-translate-x-full"
      : "md:translate-x-full";

  const sheetRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const isSwipeClosingRef = useRef(false);

  const animateClose = useCallback(() => {
    if (isSwipeClosingRef.current) return;
    setIsVisible(false);
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setIsMounted(false), CLOSE_DURATION_MS);
  }, []);

  useEffect(() => {
    if (isOpen) {
      isSwipeClosingRef.current = false;
      // Mount in the effect, NOT via a render-phase prevIsOpen comparison —
      // that pattern (added 2026-07-01) lost the mount under StrictMode
      // double-rendering and left drawers permanently rendering null.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsMounted(true);
      // One real paint frame between mount and the class change so the CSS
      // transition always plays.
      closeTimerRef.current = setTimeout(() => setIsVisible(true), 16);
    } else {
      animateClose();
    }
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, [isOpen, animateClose]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onCloseRef.current?.();
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen]);

  // ─── Drag to dismiss (mobile bottom-sheet only; desktop is mouse-driven) ────
  const touchStartY = useRef<number>(0);
  const currentDragY = useRef<number>(0);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartY.current = e.changedTouches[0]?.clientY ?? 0;
    currentDragY.current = 0;
    if (sheetRef.current) sheetRef.current.style.transition = "none";
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    const delta = Math.max(0, (e.changedTouches[0]?.clientY ?? 0) - touchStartY.current);
    currentDragY.current = delta;
    if (sheetRef.current) sheetRef.current.style.transform = `translateY(${delta}px)`;
    if (backdropRef.current) {
      const opacity = Math.max(0, 0.4 - (delta / 300) * 0.4);
      backdropRef.current.style.opacity = String(opacity);
    }
  };

  const handleTouchEnd = () => {
    const delta = currentDragY.current;
    if (sheetRef.current) sheetRef.current.style.transition = "";

    if (delta > SWIPE_CLOSE_THRESHOLD_PX) {
      if (sheetRef.current) sheetRef.current.style.transform = "translateY(100%)";
      if (backdropRef.current) {
        backdropRef.current.style.transition = `opacity ${CLOSE_DURATION_MS}ms ease-in`;
        backdropRef.current.style.opacity = "0";
      }
      setTimeout(() => {
        if (sheetRef.current) sheetRef.current.style.transform = "";
        if (backdropRef.current) {
          backdropRef.current.style.transition = "";
          backdropRef.current.style.opacity = "";
        }
        isSwipeClosingRef.current = true;
        setIsMounted(false);
        onCloseRef.current?.();
      }, CLOSE_DURATION_MS);
    } else {
      if (sheetRef.current) sheetRef.current.style.transform = "";
      if (backdropRef.current) backdropRef.current.style.opacity = "";
      currentDragY.current = 0;
    }
  };

  const handleTouchCancel = () => {
    if (sheetRef.current) {
      sheetRef.current.style.transition = "";
      sheetRef.current.style.transform = "";
    }
    if (backdropRef.current) backdropRef.current.style.opacity = "";
    currentDragY.current = 0;
  };
  // ───────────────────────────────────────────────────────────────────────────

  if (!isMounted) return null;
  if (typeof document === "undefined") return null;

  const iconTileClass =
    iconTone === "accent"
      ? "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] bg-[color:var(--accent-500)] text-[color:var(--on-accent,#fff)]"
      : "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] bg-[color:var(--surface-alt)] text-[color:var(--text-muted)]";

  // Portal to <body> so the fixed positioning anchors to the viewport even
  // under a transformed ancestor (e.g. a page-transition container).
  return createPortal(
    <>
      {/* Backdrop — lighter on desktop so the list behind stays readable while filtering */}
      <div
        ref={backdropRef}
        className={[
          "fixed inset-0 z-40 bg-black/40 transition-opacity md:bg-black/25",
          isVisible ? "opacity-100 duration-[450ms]" : "opacity-0 duration-[250ms]",
        ].join(" ")}
        aria-hidden="true"
        onClick={() => onCloseRef.current?.()}
      />

      {/* Positioner — mobile: bottom; desktop: inset floating drawer at inline-end */}
      <div className="pointer-events-none fixed inset-0 z-50 flex items-end md:items-stretch md:justify-end md:p-3.5">
        <div
          ref={sheetRef}
          role="dialog"
          aria-modal="true"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchCancel}
          className={[
            "pointer-events-auto flex w-full flex-col",
            // Panel sits on the app "content" gray so the body reads as a white
            // inset card. Falls back to the surface colour where --content is
            // undefined.
            "border border-[color:var(--border-light)] bg-[color:var(--content,var(--surface))] shadow-[var(--shadow-lg)]",
            // Mobile: bottom sheet
            "max-h-[92vh] rounded-t-2xl",
            // Desktop: inset floating drawer — full height (minus positioner
            // padding), fixed width, all corners rounded.
            `md:h-full md:max-h-none md:rounded-[18px] ${desktopWidthClass[size]}`,
            "transition-all will-change-transform",
            isVisible
              ? // Open at rest — close curve (fast, ease-in) runs from here
                "translate-y-0 md:translate-x-0 duration-[250ms] ease-in"
              : // Closed — open curve (slow expo-out) runs from here. Mobile slides
                // up (translate-y); desktop slides in from the inline-end edge.
                `translate-y-full md:translate-y-0 ${closedSlideX} duration-[250ms] ease-[cubic-bezier(0.4,0,1,1)]`,
          ].join(" ")}
        >
          {/* Drag handle — mobile only */}
          <div className="flex shrink-0 justify-center pb-1 pt-2.5 md:hidden" aria-hidden="true">
            <div className="h-1 w-10 rounded-full bg-[color:var(--border-light)]" />
          </div>

          {/* Header */}
          {title || icon ? (
            <div className="flex shrink-0 items-start justify-between gap-3 px-4 pb-3 pt-[13px]">
              <div className="flex min-w-0 items-center gap-2.5">
                {icon ? <span className={iconTileClass}>{icon}</span> : null}
                <div className="min-w-0">
                  {title ? (
                    <h3 className="truncate text-[15px] font-bold text-[color:var(--text-primary)]">
                      {title}
                    </h3>
                  ) : null}
                  {subtitle ? (
                    <p className="mt-0.5 text-[12px] text-[color:var(--text-muted)]">
                      {subtitle}
                    </p>
                  ) : null}
                </div>
              </div>
              <button
                type="button"
                onClick={() => onCloseRef.current?.()}
                aria-label={closeLabel ?? ""}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] border border-[color:var(--border-light)] bg-[color:var(--surface)] text-[color:var(--text-muted)] transition-colors hover:bg-[color:var(--hover,var(--surface-alt))] hover:text-[color:var(--text-primary)]"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <path d="M6 6l12 12M6 18L18 6" />
                </svg>
              </button>
            </div>
          ) : null}

          {/* Body content floats as a white inset card on the gray panel */}
          <div className="flex min-h-0 flex-1 px-3">
            <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto rounded-[14px] border border-[color:var(--card-br,var(--border-light))] bg-[color:var(--surface)] p-4">
              {children}
            </div>
          </div>

          {/* Sticky footer — on the gray panel, no divider */}
          {footerActions ? (
            <div className="flex shrink-0 items-center gap-2 px-4 pb-[calc(0.875rem+env(safe-area-inset-bottom))] pt-3">
              {footerActions}
            </div>
          ) : (
            <div
              aria-hidden="true"
              className="h-[calc(0.5rem+env(safe-area-inset-bottom))] shrink-0"
            />
          )}
        </div>
      </div>
    </>,
    document.body,
  );
}
